import env from '@/constants/env';
import axios from 'axios';
import {refreshToken} from '@/components/utils/axiosInstance';

// Make an 'instance' of axios
const axiosHttp = axios.create({
  // where we make our configurations
  baseURL: env.ApiServerUrl || window.location.origin
});

let isRefreshing = false;
let failedRequestsQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedRequestsQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedRequestsQueue = [];
};

// Add configure interceptors && all the other cool stuff
axiosHttp.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      if (
        error.response.data.code === 'token_not_valid' &&
        !originalRequest._retry
      ) {
        if (isRefreshing) {
          // Queue this request while refresh is in progress
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({resolve, reject});
          })
            .then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosHttp(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshToken();
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosHttp(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Only redirect to login if refresh fails
          localStorage.clear();
          window.location.href = '/authentication/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject(error as unknown as Error);
    }

    if (error.response.status === 404) {
      const newErr = error;

      newErr.response.data = {
        message: 'route_404'
      };

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject(newErr as unknown as Error);
    }

    if (error.response.status === 500) {
      const newErr = error;

      newErr.response.data = {
        message: 'server_error_msg'
      };

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject(newErr as unknown as Error);
    }

    if (error.response.status === 503) {
      const newErr = error;

      newErr.response.data = {
        message: 'server_error_msg'
      };

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject(newErr as unknown as Error);
    }

    return Promise.reject(error);
  }
);

export default axiosHttp;
